import { COUNTRY, COUNTRY_CONFIG_WITH_LANG, LANGUAGE } from "@type/index";
import store from "../../redux/store";

const MILLISECONDS = 1000;
const SEC = 60;
const MINUTE = SEC * MILLISECONDS;

type CountryConfig = Partial<Config>;
type LanguageConfig<T> = Partial<Partial<Record<LANGUAGE, T>>>;
type Config = {
  countryRegion: string;
  /* [COUNTRY, LANGUAGE] configs */
  timeZone: LanguageConfig<{
    defaultTimezone: string;
    offset: {
      [key: string]: number;
    };
  }>;
  timeZoneParentCategory: {
    [key: string]: string;
  };
};

type timeZoneParentCategoryType = {
  [key: string]: string;
};

const timeZoneParentCategory: timeZoneParentCategoryType = {
  EST: "ET",
  EDT: "ET"
};

const defaultConfig = {
  countryRegion: "Asia/Tokyo",
  timeZone: {},
  timeZoneParentCategory: {
    EST: "ET",
    EDT: "ET"
  }
};

type COUNTRY_CONFIG_WITH_REGION = {
  [key in COUNTRY]: CountryConfig;
};
const COUNTRY_CONFIG: COUNTRY_CONFIG_WITH_REGION = {
  [COUNTRY.AU]: {
    countryRegion: "Australia/Sydney",
    timeZone: {
      [LANGUAGE.EN]: {
        defaultTimezone: "AEST",
        offset: {
          AEST: 10,
          AEDT: 11
        }
      }
    }
  },
  [COUNTRY.CA]: {
    countryRegion: "Canada/Eastern",
    timeZone: {
      [LANGUAGE.EN]: {
        defaultTimezone: "EST",
        offset: {
          EST: -5,
          EDT: -4
        }
      },
      [LANGUAGE.FR]: {
        defaultTimezone: "HNE",
        offset: {
          HNE: -5,
          HAE: -4
        }
      }
    }
  },
  [COUNTRY.ID]: {
    countryRegion: "Asia/Jakarta",
    timeZone: {
      [LANGUAGE.EN]: {
        defaultTimezone: "WIB",
        offset: {
          WIB: 7
        }
      },
      [LANGUAGE.ID]: {
        defaultTimezone: "WIB",
        offset: {
          WIB: 7
        }
      }
    }
  },
  [COUNTRY.IN]: {
    countryRegion: "Asia/Kolkata",
    timeZone: {
      [LANGUAGE.EN]: {
        defaultTimezone: "IST",
        offset: {
          IST: 5.5
        }
      }
    }
  },
  [COUNTRY.MY]: {
    countryRegion: "Asia/Kuala_Lumpur",
    timeZone: {
      [LANGUAGE.EN]: {
        defaultTimezone: "MYT",
        offset: {
          MYT: 8
        }
      }
    }
  },
  [COUNTRY.PH]: {
    countryRegion: "Asia/Manila",
    timeZone: {
      [LANGUAGE.EN]: {
        defaultTimezone: "PHT",
        offset: {
          PHT: 8
        }
      }
    }
  },
  [COUNTRY.SG]: {
    countryRegion: "Asia/Singapore",
    timeZone: {
      [LANGUAGE.EN]: {
        defaultTimezone: "SGT",
        offset: {
          SGT: 8
        }
      }
    }
  },
  [COUNTRY.TH]: {
    countryRegion: "Asia/Bangkok",
    timeZone: {
      [LANGUAGE.EN]: {
        defaultTimezone: "ICT",
        offset: {
          ICT: 7
        }
      },
      [LANGUAGE.TH]: {
        defaultTimezone: "ICT",
        offset: {
          ICT: 7
        }
      }
    }
  },
  [COUNTRY.VN]: {
    countryRegion: "Asia/Ho_Chi_Minh",
    timeZone: {
      [LANGUAGE.EN]: {
        defaultTimezone: "ICT",
        offset: {
          ICT: 7
        }
      },
      [LANGUAGE.VI]: {
        defaultTimezone: "ICT",
        offset: {
          ICT: 7
        }
      }
    }
  }
};

export const initializeCountryConfig = (_country: COUNTRY) =>
  COUNTRY_CONFIG[_country];

const getTimeZone = (countryConfig: CountryConfig, language: LANGUAGE) =>
  countryConfig.timeZone?.[language] ?? { defaultTimezone: "", offset: {} };

const getCountryRegion = (countryConfig: CountryConfig) =>
  countryConfig.countryRegion ?? defaultConfig.countryRegion;

const toTimeForTimeZone = (
  country: COUNTRY,
  language: LANGUAGE,
  date: Date
): {
  date: number;
  timeZoneName: string;
} => {
  const countryConfig = initializeCountryConfig(country);
  const timestamp: number = date.getTime();
  let intlDate;
  try {
    intlDate =
      window.Intl &&
      window.Intl.DateTimeFormat(
        `${language.toLowerCase()}-${country.toUpperCase()}`,
        {
          timeZone: getCountryRegion(countryConfig),
          timeZoneName: "short"
        }
      )
        .format(timestamp)
        .split(" ");
  } catch (error) {
    // IE11 does not support IANA time zone names (e.g. `ASIA/TOKYO`) - use `getTimeZone` fallback
  }
  const timeZoneName: string =
    intlDate && intlDate.length
      ? intlDate[intlDate.length - 1]
      : getTimeZone(countryConfig, language).defaultTimezone;

  const utc = timestamp + date.getTimezoneOffset() * MINUTE;

  // Add offset (-5/-4) to UTC time to convert to EST/EDT
  const timeZone =
    getTimeZone(countryConfig, language).offset[timeZoneName] || 0;
  const dateWithOffset = utc + SEC * MINUTE * timeZone;
  const timeZoneValidatedName =
    timeZoneParentCategory[timeZoneName] ?? timeZoneName;
  return { date: dateWithOffset, timeZoneName: timeZoneValidatedName };
};

export const dateFormat: COUNTRY_CONFIG_WITH_LANG<string> = {
  [COUNTRY.AU]: {
    [LANGUAGE.EN]: "<dd>/<mm>/<yyyy>"
  },
  [COUNTRY.CA]: {
    [LANGUAGE.EN]: "<MM>/<DD>/<yyyy>",
    [LANGUAGE.FR]: "<dd>/<mm>/<yyyy>"
  },
  [COUNTRY.ID]: {
    [LANGUAGE.EN]: "<dd>/<mm>/<yyyy>",
    [LANGUAGE.ID]: "<dd>/<mm>/<yyyy>"
  },
  [COUNTRY.IN]: {
    [LANGUAGE.EN]: "<dd>/<mm>/<yyyy>"
  },
  [COUNTRY.MY]: {
    [LANGUAGE.EN]: "<dd>/<mm>/<yyyy>"
  },
  [COUNTRY.PH]: {
    [LANGUAGE.EN]: "<MM>/<DD>/<yyyy>"
  },
  [COUNTRY.SG]: {
    [LANGUAGE.EN]: "<dd>/<mm>/<yyyy>"
  },
  [COUNTRY.TH]: {
    [LANGUAGE.EN]: "<dd>/<mm>/<yyyy>",
    [LANGUAGE.TH]: "<dd>/<mm>/<yyyy>"
  },
  [COUNTRY.VN]: {
    [LANGUAGE.EN]: "<dd>/<mm>/<yyyy>",
    [LANGUAGE.VI]: "<dd>/<mm>/<yyyy>"
  }
};

export const getDateFormat = (country: COUNTRY, language: LANGUAGE) =>
  dateFormat[country][language] || "";

export function convertToDatetime(date?: number): number {
  if (date) {
    // Convert from seconds to milliseconds.
    // If the timestamp is 10 digits, it needs to be multiplied by 1000. If the timestamp is 13 digits, it does not need
    return date.toString().length === 10 ? date * 1000 : date;
  }
  return 0;
}

export const formatDate = (time: number) => {
  const { region, language } = store.getState().config;
  let dateObj = new Date(convertToDatetime(time));
  // takes date format from country configuration
  const _country = region as COUNTRY;
  const _language = language as LANGUAGE;
  const newDateObj = toTimeForTimeZone(_country, _language, dateObj).date;
  dateObj = new Date(convertToDatetime(newDateObj));
  const newDate = getDateFormat(_country, _language);
  const date = dateObj.getDate();
  const month = dateObj.getMonth() + 1;
  const year = dateObj.getFullYear();

  const formattedDate = newDate
    .replace("<dd>", `0${date}`.slice(-2))
    .replace("<DD>", `${date}`)
    .replace("<mm>", `0${month}`.slice(-2))
    .replace("<MM>", `${month}`)
    .replace("<yyyy>", `${year}`);
  return formattedDate;
};
